import { TextField } from "@asurion-core/aui-react-fields";
import {
  Action,
  Icon,
  InlineLoader,
  ManualSelectionChips,
  Text,
  breakpoints,
  useClickOutside
} from "@asurion-core/mx-asurion-ui-react";
import { PartDetailModal, useDebounce, useNavigateWithParams } from "@ubif/components";
import {
  ChipOption,
  Client,
  CustomerType,
  FOGetInventoryResponse,
  FOStoreInventory,
  GlobalSearchCategory,
  ItemCatalogCSUSearchResponse,
  LayoutFeature,
  chipOptions
} from "@ubif/types";
import {
  PriceQuoteSearchResult,
  ScanDetectedEvent,
  SearchArrivalsResponseData,
  SearchCustomerPriceQuoteRequest,
  SearchCustomersResponse,
  SearchPriceQuoteResponse,
  SearchWorkordersResponse,
  WORKORDER_SEARCH_DEFAULT,
  Workorder,
  checkScanId,
  fullStory,
  parseWorkorderId,
  selectCurrentStore,
  selectLayoutFeature,
  setSearchArrivalsResponse,
  setSearchCategory,
  setSearchCriteria,
  setSearchCriteriaRaw,
  setSearchCustomersResponse,
  setSearchPartsResponse,
  setSearchPriceQuoteResponse,
  setSearchSerialResponse,
  setSearchWorkordersResponse,
  setShowSearchBox,
  setShowSearchResults,
  useGlobalDispatch,
  useListenScanDetected,
  useSearchArrivalsQuery,
  useSearchCustomersQuery,
  useSearchPartsQuery,
  useSearchPriceQuoteQuery,
  useSearchSalesordersQuery,
  useSearchSerialNumbersQuery,
  useSearchWorkordersQuery,
  useTypedSelector
} from "@ubif/util-store";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { StyledLoader } from "./elements/FlexContainers";
import { QuickSearchArrivalDetails } from "./elements/QuickSearchArrivalDetails";
import { QuickSearchCustomerDetails } from "./elements/QuickSearchCustomerDetails";
import { QuickSearchPartsDetails } from "./elements/QuickSearchPartsDetails";
import { QuickSearchPriceQuoteDetails } from "./elements/QuickSearchPriceQuote";
import { QuickSearchSalesorderDetails } from "./elements/QuickSearchSalesorderDetails";
import { QuickSearchSerialDetails } from "./elements/QuickSearchSerialDetails";
import { QuickSearchWorkorderDetails } from "./elements/QuickSearchWorkorderDetails";
import { shouldSkipSerialSearch } from "./serialSearchQueries";
import { shouldSkipWorkorderSearch } from "./workorderSearchQueries";

const NUM_RECORDS_TO_SHOW = 5;
export type GlobalSearchProps = {
  showSearchBox: boolean;
  searchCategory: GlobalSearchCategory;
  searchCriteria: string;
  searchCriteriaRaw: string;
  searchedCustomersData: SearchCustomersResponse;
  searchedWorkordersData: Workorder[];
  searchedArrivalsData: SearchArrivalsResponseData;
  searchedPartsData: ItemCatalogCSUSearchResponse[];
  isSearchInProgress: boolean;
  searchPriceQuote: SearchPriceQuoteResponse;
  searchedSerialData: FOGetInventoryResponse;
};

export const GlobalSearch = ({
  showSearchBox,
  searchCategory,
  searchCriteria,
  searchCriteriaRaw,
  searchedCustomersData,
  searchedWorkordersData,
  searchedArrivalsData,
  searchedPartsData,
  isSearchInProgress,
  searchPriceQuote,
  searchedSerialData
}: GlobalSearchProps) => {
  const { t } = useTranslation("root-config");
  const dispatch = useGlobalDispatch();
  const navigate = useNavigate();
  const navigateWithParams = useNavigateWithParams();
  const store = useTypedSelector(selectCurrentStore);
  const dashboardPriceCheckEnabled = useTypedSelector(selectLayoutFeature(LayoutFeature.dashboard_pricecheck_enabled));

  const [salesId, setSalesId] = useState<string>();
  const [showQuickSearchOverlay, setShowQuickSearchOverlay] = useState<boolean>(false);
  const globalSearchRef = useRef<HTMLDivElement>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const getSearchInput = () => searchBoxRef.current?.querySelector("input");
  const debouncedSearchCriteria = useDebounce(searchCriteriaRaw.trim(), 500);
  const priceQuote = searchPriceQuote ? searchPriceQuote[0] : null;

  const [showPartDetailModal, setShowPartDetailModal] = useState(false);
  const [activePart, setActivePart] = useState<FOStoreInventory>(null);

  const openPartDetailModal = () => {
    if (!searchedSerialData) return;

    setActivePart({
      ItemNumber: searchedSerialData.ItemNumber,
      SerialNumber: debouncedSearchCriteria,
      SerialStatus: searchedSerialData.SerialStatus,
      Store: searchedSerialData.Store,
      Vendor: searchedSerialData.Vendor,
      WarehouseId: searchedSerialData.Warehouse
    });

    setShowPartDetailModal(true);
  };

  useEffect(() => {
    setShowQuickSearchOverlay(true);
  }, [priceQuote]);

  const skipGetWorkordersCall = (
    searchCategory: GlobalSearchCategory,
    searchedCustomersData: SearchCustomersResponse
  ): boolean => shouldSkipWorkorderSearch(searchCategory, debouncedSearchCriteria, salesId, searchedCustomersData);

  useSearchPartsQuery(
    {
      searchTerm: debouncedSearchCriteria
    },
    {
      skip: searchCategory !== "parts" || !debouncedSearchCriteria || debouncedSearchCriteria?.length < 3
    }
  );

  useSearchSerialNumbersQuery(
    {
      serial: debouncedSearchCriteria,
      includeQuarantineInfo: false,
      includeSalvageInfo: false
    },
    { skip: shouldSkipSerialSearch(searchCategory, debouncedSearchCriteria) }
  );

  const scanPart = (scanEvent: ScanDetectedEvent) => {
    const barcode = scanEvent.scan;

    if (barcode && barcode.length === 12) {
      setSalesId(barcode);
    }
    // TODO - part barcodes are not always length of 17
    if (barcode && searchCategory === chipOptions.parts.searchCategory && barcode.length === 17) {
      const sku = barcode.split("-")[0];
      dispatch(setSearchCriteriaRaw(sku));
      dispatch(setSearchCriteria(barcode));
    }
  };

  useSearchCustomersQuery(debouncedSearchCriteria, {
    skip:
      searchCategory !== "customers" ||
      !debouncedSearchCriteria ||
      debouncedSearchCriteria?.length < 3 ||
      checkScanId("PE", debouncedSearchCriteria)
  });

  const skipPriceQuoteCall = (): boolean => {
    if (!dashboardPriceCheckEnabled) return true;
    if (
      searchCategory !== chipOptions.customers.searchCategory ||
      !debouncedSearchCriteria ||
      debouncedSearchCriteria?.length < 3
    ) {
      return true;
    }
    if (!checkScanId("PE", debouncedSearchCriteria) && !searchedCustomersData) return true;
    return false;
  };

  const getSearchPriceQuotePayload = (): SearchCustomerPriceQuoteRequest => {
    if (checkScanId("PE", debouncedSearchCriteria)) {
      return { quoteId: debouncedSearchCriteria.replace(/^PE/i, ""), isCrossStoreSearch: true };
    } else if (searchedCustomersData) {
      return { customerId: searchedCustomersData[0]?.id, isCrossStoreSearch: true };
    } else {
      return {};
    }
  };

  useSearchPriceQuoteQuery(getSearchPriceQuotePayload(), {
    skip: skipPriceQuoteCall()
  });

  const generatePayloadForWorkorderSearch = () => {
    if (
      (searchCategory === chipOptions.workorders.searchCategory ||
        searchCategory === chipOptions.claims.searchCategory) &&
      (salesId || checkScanId("SO", debouncedSearchCriteria))
    ) {
      return { scanId: (salesId || debouncedSearchCriteria).toUpperCase(), ...WORKORDER_SEARCH_DEFAULT };
    } else if (
      searchCategory === chipOptions.customers.searchCategory &&
      searchedCustomersData &&
      !salesId &&
      searchedCustomersData[0]?.id
    ) {
      return { customerId: searchedCustomersData[0].id, ...WORKORDER_SEARCH_DEFAULT };
    } else if (searchCategory === chipOptions.workorders.searchCategory && !salesId) {
      const workorderId = parseWorkorderId(debouncedSearchCriteria);
      return workorderId === undefined ? undefined : { workorderId, ...WORKORDER_SEARCH_DEFAULT };
    } else if (searchCategory === chipOptions.claims.searchCategory && !salesId) {
      return { claimNumber: debouncedSearchCriteria, ...WORKORDER_SEARCH_DEFAULT };
    }
  };

  const workorderSearchPayload = generatePayloadForWorkorderSearch();

  useSearchWorkordersQuery(
    {
      ...workorderSearchPayload
    },
    {
      // Without a payload the request would fetch every workorder, so an unusable term fetches nothing.
      skip: !workorderSearchPayload || skipGetWorkordersCall(searchCategory, searchedCustomersData)
    }
  );

  const salesOrdersPayload = { ...workorderSearchPayload };
  const { data: salesOrdersData } = useSearchSalesordersQuery(salesOrdersPayload, {
    skip: !salesOrdersPayload?.["customerId"] || skipGetWorkordersCall(searchCategory, searchedCustomersData)
  });

  useSearchArrivalsQuery(
    {
      customerId: searchedCustomersData && searchedCustomersData[0]?.id,
      claimNumber: searchCategory === chipOptions.claims.searchCategory ? debouncedSearchCriteria : undefined,
      isCrossStoreSearch: true
    },
    {
      skip: skipGetWorkordersCall(searchCategory, searchedCustomersData)
    }
  );

  const priceQuoteCard = (priceQuote: PriceQuoteSearchResult) => (
    <QuickSearchPriceQuoteDetails
      fullName={priceQuote.fullName}
      email={priceQuote.primaryEmail}
      phone={priceQuote.primaryPhone}
      canEmail={!!priceQuote.canEmail}
      canSms={!!priceQuote.canSms}
      canCall={!!priceQuote.canCall}
      highlightText={priceQuote.quoteId.toString()}
      preferredContactLabel={t("GlobalSearch.ContactPreference")}
      quoteId={priceQuote.quoteId}
      totalCost={priceQuote.totalCost}
      minCost={priceQuote.minCost}
      maxCost={priceQuote.maxCost}
    ></QuickSearchPriceQuoteDetails>
  );
  const handleGlobalSearchOutsideClick = () => {
    if (showQuickSearchOverlay) {
      setShowQuickSearchOverlay(false);
    }
  };

  useClickOutside(handleGlobalSearchOutsideClick, globalSearchRef);

  const handleChipSelection = useCallback(
    (chipDetails: ChipOption) => {
      dispatch(setSearchCategory(chipDetails.searchCategory));
      dispatch(setSearchCriteria(""));
      dispatch(setSearchCriteriaRaw(""));
      dispatch(setSearchCustomersResponse(undefined));
      dispatch(setSearchWorkordersResponse(undefined));
      dispatch(setSearchArrivalsResponse(undefined));
      dispatch(setSearchPartsResponse(undefined));
      dispatch(setSearchPriceQuoteResponse(undefined));
      dispatch(setSearchSerialResponse(undefined));
      setSalesId("");
      getSearchInput()?.focus();
    },
    [dispatch]
  );
  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputString: string = event.target.value;
      if (searchPriceQuote) dispatch(setSearchPriceQuoteResponse(undefined));

      if (searchedCustomersData) {
        dispatch(setSearchCustomersResponse(undefined));
      }
      if (searchedWorkordersData) {
        dispatch(setSearchWorkordersResponse(undefined));
      }
      if (searchedArrivalsData) {
        dispatch(setSearchArrivalsResponse(undefined));
      }
      if (searchedPartsData) {
        dispatch(setSearchPartsResponse(undefined));
      }
      if (searchedSerialData) {
        dispatch(setSearchSerialResponse(undefined));
      }
      if (salesId) {
        setSalesId("");
      }
      if (searchCategory === chipOptions.parts.searchCategory && inputString.includes("-")) {
        const inputString1 = inputString.split("-")[0];
        const sku = inputString1;
        const skuWithSerial = inputString.replace("-", "");
        if (
          skuWithSerial.length === 16 &&
          typeof skuWithSerial === "string" &&
          !Number.isNaN(Number(skuWithSerial)) &&
          sku.length === 6
        ) {
          dispatch(setSearchCriteriaRaw(inputString1));
        } else {
          dispatch(setSearchCriteriaRaw(inputString));
        }
        dispatch(setSearchCriteria(inputString));
      } else {
        dispatch(setSearchCriteria(inputString));
        dispatch(setSearchCriteriaRaw(inputString));
      }
    },
    [searchCategory, searchedCustomersData, searchedWorkordersData, searchedArrivalsData, dispatch]
  );

  const redirectToSearchResults = useCallback(() => {
    dispatch(setShowSearchResults(true));
    setShowQuickSearchOverlay(false);
    fullStory((FS) => FS("trackEvent", { name: "Search Completed", properties: {} }));
    getSearchInput()?.blur();
  }, [dispatch, searchCategory]);

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" && searchCategory === "customers") {
      if (customersToDisplay?.length === 1 && !isSearchInProgress) {
        handleCustomerSelection(
          customersToDisplay[0].id,
          customersToDisplay[0]?.clientId,
          customersToDisplay[0]?.customerTypeId
        );
      } else {
        redirectToSearchResults();
      }
    }
  };

  // resetting the global search
  const handleAffixRightClick = () => {
    dispatch(setSearchCriteriaRaw(""));
    dispatch(setShowSearchBox(false));
    dispatch(setSearchCategory("customers"));
    dispatch(setSearchCriteria(""));
    dispatch(setShowSearchResults(false));
    dispatch(setSearchCustomersResponse(undefined));
    dispatch(setSearchWorkordersResponse(undefined));
    dispatch(setSearchArrivalsResponse(undefined));
    dispatch(setSearchPartsResponse(undefined));
    dispatch(setSearchSerialResponse(undefined));
    dispatch(setSearchPriceQuoteResponse(undefined));
    fullStory((FS) => FS("trackEvent", { name: "Search Reset", properties: {} }));
  };

  const handleCustomerSelection = (customerId: string, clientId: Client = Client.UBIF, customerTypeId?: number) => {
    if (customerTypeId === CustomerType.B2B.valueOf()) {
      navigate(`/admin/business-clients/${customerId}`);
    } else {
      navigateWithParams(`/customer/${customerId}`, {
        searchTerm: debouncedSearchCriteria,
        showBreadcrumb: (searchedCustomersData?.length > 1).toString(),
        clientId: clientId.toString()
      });
    }
    setShowQuickSearchOverlay(false);
    dispatch(setShowSearchResults(false));
  };

  const handleWorkorderSelection = (workorderId: number) => {
    if (!workorderId || workorderId <= 0) {
      return;
    }
    navigate(`/repair/workorder/${workorderId}`);
    setShowQuickSearchOverlay(false);
    dispatch(setShowSearchResults(false));
  };

  const handleArrivalSelection = (arrivalId: number) => {
    navigate(`/check-in/arrivals/${arrivalId}`);
    setShowQuickSearchOverlay(false);
    dispatch(setShowSearchResults(false));
  };
  const handleSalesorderSelection = (salesOrder: string) => {
    navigate(`/check-out/refund/${salesOrder}`);
    setShowQuickSearchOverlay(false);
    dispatch(setShowSearchResults(false));
  };

  const handlePartsItemSelection = (itemNumber: string) => {
    if (!itemNumber) return;
    dispatch(setShowSearchResults(false));
    setShowQuickSearchOverlay(false);
    navigate(`/boh/inventory/store-products/${encodeURIComponent(itemNumber)}`);
  };
  const generateSearchTextLabel = () => {
    let label = "";
    switch (searchCategory) {
      case chipOptions.customers.searchCategory:
        label = t("GlobalSearch.SearchCustomerTextFieldLabel");
        break;
      case chipOptions.workorders.searchCategory:
        label = t("GlobalSearch.SearchWorkorderTextFieldLabel");
        break;
      case chipOptions.parts.searchCategory:
        label = t("GlobalSearch.SearchPartsTxtFieldLabel");
        break;
      case chipOptions.claims.searchCategory:
        label = t("GlobalSearch.SearchClaimTextFieldLabel");
        break;
      case chipOptions.serial_numbers.searchCategory:
        label = t("GlobalSearch.SearchSerialNumberTextFieldLabel");
        break;
      default:
        label = t("GlobalSearch.SearchCustomerTextFieldLabel");
        break;
    }

    return label;
  };

  const partsToDisplay = useMemo<ItemCatalogCSUSearchResponse[]>(() => {
    if (searchedPartsData?.length) {
      if (searchedPartsData.length > NUM_RECORDS_TO_SHOW) {
        return searchedPartsData.slice(0, NUM_RECORDS_TO_SHOW);
      } else {
        return searchedPartsData;
      }
    }
    return [];
  }, [searchedPartsData]);

  const customersToDisplay = useMemo<SearchCustomersResponse>(() => {
    if (searchedCustomersData?.length) {
      if (searchedCustomersData.length > NUM_RECORDS_TO_SHOW) {
        return searchedCustomersData.slice(0, NUM_RECORDS_TO_SHOW);
      } else {
        return searchedCustomersData;
      }
    }
    return [];
  }, [searchedCustomersData]);

  const showPriceQuote = () => {
    return !(searchCategory === "customers" && priceQuote.workorderId && !checkScanId("PE", debouncedSearchCriteria));
  };

  const workorderToDisplay = useMemo<SearchWorkordersResponse["workOrders"]>(() => {
    if (
      searchedWorkordersData?.length &&
      (chipOptions.workorders.searchCategory === searchCategory || chipOptions.claims.searchCategory === searchCategory)
    ) {
      if (searchedWorkordersData.length > NUM_RECORDS_TO_SHOW) {
        return searchedWorkordersData.slice(0, NUM_RECORDS_TO_SHOW);
      } else {
        return searchedWorkordersData;
      }
    }
    return [];
  }, [searchedWorkordersData]);

  const generateSearchFooter = (data: SearchCustomersResponse | SearchWorkordersResponse["workOrders"]) => {
    return data?.length > 1 ? (
      <StyledViewResults onClick={redirectToSearchResults}>
        <Icon.SSR src="search-magnify-glass" size="large" />
        <div className="view-results-text">
          {data.length > NUM_RECORDS_TO_SHOW ? t("GlobalSearch.ViewAllResults") : t("GlobalSearch.ViewResults")}
          <Text as="h3" size={2} weight="heavy">
            {debouncedSearchCriteria}
          </Text>
        </div>
      </StyledViewResults>
    ) : null;
  };

  useListenScanDetected(scanPart);

  return (
    <>
      {!showSearchBox ? (
        <Action
          variant="flat"
          onClick={() => {
            fullStory((FS) => FS("trackEvent", { name: "Search Started", properties: {} }));
            dispatch(setShowSearchBox(true));
          }}
        >
          <Icon.SSR src="search-magnify-glass" />
          {t("GlobalSearch.SearchButton")}
        </Action>
      ) : (
        <StyledDiv shouldScroll={searchedPartsData?.length > 0} ref={globalSearchRef}>
          <div className="search-field">
            <div ref={searchBoxRef}>
              <TextField className="search-text-field">
                <TextField.Label>{generateSearchTextLabel()}</TextField.Label>
                <TextField.Leading>
                  <Icon.SSR src="search-magnify-glass" />
                </TextField.Leading>
                <TextField.Input
                  onFocus={() => setShowQuickSearchOverlay(true)}
                  value={searchCriteria}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  autoFocus
                  autoComplete="off"
                />
                <TextField.Trailing onClick={handleAffixRightClick}>
                  <Icon.SSR src="close-x" />
                </TextField.Trailing>
              </TextField>
            </div>
            {isSearchInProgress && (
              <div className="search-field-loader">
                <InlineLoader title="Loading" width="20px" />
              </div>
            )}
          </div>
          {showPartDetailModal && (
            <PartDetailModal
              part={activePart}
              preloadedInventoryResult={searchedSerialData}
              showModal={showPartDetailModal}
              setShowModal={setShowPartDetailModal}
            />
          )}

          {showQuickSearchOverlay ? (
            <QuickSearchOverlay>
              <ChipsContainer>
                <Text as="span" className="text-looking-for" size={2}>
                  {t("GlobalSearch.ChipContainerTitle")}
                </Text>
                <ManualSelectionChips
                  chips={[
                    {
                      hideDefaultIcon: false,
                      isSelected: searchCategory === chipOptions.customers.searchCategory,
                      label: chipOptions.customers.chipLabel,
                      onClick: () => {
                        handleChipSelection(chipOptions.customers);
                      }
                    },
                    {
                      hideDefaultIcon: false,
                      isSelected: searchCategory === chipOptions.workorders.searchCategory,
                      label: chipOptions.workorders.chipLabel,
                      onClick: () => {
                        handleChipSelection(chipOptions.workorders);
                      }
                    },
                    {
                      hideDefaultIcon: false,
                      isSelected: searchCategory === chipOptions.parts.searchCategory,
                      label: chipOptions.parts.chipLabel,
                      onClick: () => {
                        handleChipSelection(chipOptions.parts);
                      }
                    },
                    {
                      hideDefaultIcon: false,
                      isSelected: searchCategory === chipOptions.claims.searchCategory,
                      label: chipOptions.claims.chipLabel,
                      onClick: () => {
                        handleChipSelection(chipOptions.claims);
                      }
                    },
                    {
                      hideDefaultIcon: false,
                      isSelected: searchCategory === chipOptions.serial_numbers.searchCategory,
                      label: chipOptions.serial_numbers.chipLabel,
                      onClick: () => {
                        handleChipSelection(chipOptions.serial_numbers);
                      }
                    }
                    // comment out for Gulch store  release
                    // {
                    //   hideDefaultIcon: false,
                    //   isSelected: searchCategory === chipOptions.accessories.searchCategory,
                    //   label: chipOptions.accessories.chipLabel,
                    //   onClick: () => {
                    //     handleChipSelection(chipOptions.accessories);
                    //   }
                    // },
                    // {
                    //   hideDefaultIcon: false,
                    //   isSelected: searchCategory === chipOptions.non_repair_services.searchCategory,
                    //   label: chipOptions.non_repair_services.chipLabel,
                    //   onClick: () => {
                    //     handleChipSelection(chipOptions.non_repair_services);
                    //   }
                    // }
                  ]}
                  layout="wrap"
                  options={{
                    edgeItemsMargin: "0px"
                  }}
                  size="small"
                  type="selection"
                  variant="normal"
                />
              </ChipsContainer>
              {isSearchInProgress && (
                <StyledLoader height={10}>
                  <InlineLoader width="2rem" title="Searching" />
                </StyledLoader>
              )}
              {!searchedCustomersData?.length && priceQuote && showPriceQuote() ? priceQuoteCard(priceQuote) : null}
              {customersToDisplay?.length ? (
                <>
                  <GreyHorizontalDivider />
                  <QuickSearchResultContainer>
                    {customersToDisplay.map((customer) => (
                      <QuickSearchCustomerDetails
                        key={`${customer.id}:${customer.clientId ?? 1}`}
                        customerId={customer.id}
                        firstName={customer.firstName}
                        lastName={customer.lastName}
                        fullName={customer.fullName}
                        email={customer.primaryEmail}
                        phone={customer.primaryPhone}
                        canCall={false}
                        canEmail={!!customer.canEmail}
                        canSms={false}
                        highlightText={debouncedSearchCriteria}
                        clientId={customer?.clientId ?? 1}
                        preferredContactLabel={t("GlobalSearch.ContactPreference")}
                        customerTypeId={customer?.customerTypeId}
                        onClick={handleCustomerSelection}
                      />
                    ))}
                    {priceQuote && showPriceQuote() ? priceQuoteCard(priceQuote) : null}
                    {searchedWorkordersData?.length ? (
                      <QuickSearchWorkorderDetails
                        fullName={searchedWorkordersData[0].customer.fullName}
                        workorderId={searchedWorkordersData[0].workorderId}
                        claimNumber={searchedWorkordersData[0].claimNumber}
                        workorderStatus={{
                          workorderStatusId: searchedWorkordersData[0].workorderStatusId,
                          name: searchedWorkordersData[0].workorderStatusName
                        }}
                        program={{
                          id: searchedWorkordersData[0].programId,
                          name: searchedWorkordersData[0].programName
                        }}
                        highlightText={debouncedSearchCriteria}
                        preferredContactLabel={t("GlobalSearch.ContactPreference")}
                        onClick={handleWorkorderSelection}
                        updatedAt={searchedWorkordersData[0].updatedAt}
                        deviceName={searchedWorkordersData[0].deviceCatalog.name}
                        isRefurbishedDeviceSale={searchedWorkordersData[0].isRefurbishedDeviceSale || false}
                        clientId={searchedWorkordersData[0].clientId}
                      />
                    ) : null}
                    {searchedArrivalsData?.length ? (
                      <QuickSearchArrivalDetails
                        fullName={searchedArrivalsData[0].customer.fullName}
                        email={searchedArrivalsData[0].customer.primaryEmail}
                        phone={searchedArrivalsData[0].customer.primaryPhone}
                        canCall={false}
                        canEmail={!!searchedArrivalsData[0].customer.canEmail}
                        canSms={false}
                        preferredContactLabel=""
                        arrival={{
                          arrivalId: searchedArrivalsData[0].arrivalId,
                          arrivalStatusId: searchedArrivalsData[0].arrivalStatus.arrivalStatusId,
                          appointment: searchedArrivalsData[0].appointment || "--",
                          isMissed: searchedArrivalsData[0]?.isMissed
                        }}
                        deviceName={searchedArrivalsData[0].customerDevice.deviceName || ""}
                        modelName={searchedArrivalsData[0].customerDevice.modelName || ""}
                        onClick={handleArrivalSelection}
                        storeTimeZone={store.storeTimeZone}
                        clientId={searchedArrivalsData[0].clientId}
                      />
                    ) : null}
                    {salesOrdersData?.length ? (
                      <QuickSearchSalesorderDetails
                        fullName={searchedCustomersData[0].fullName}
                        onClick={handleSalesorderSelection}
                        salesOrder={salesOrdersData[0]}
                      />
                    ) : null}
                    {generateSearchFooter(searchedCustomersData)}
                  </QuickSearchResultContainer>
                </>
              ) : null}
              {workorderToDisplay?.length ? (
                <>
                  <GreyHorizontalDivider />
                  {workorderToDisplay?.map((workorder) => (
                    <QuickSearchWorkorderDetails
                      key={workorder.workorderId}
                      fullName={workorder.customer.fullName}
                      workorderId={workorder.workorderId}
                      claimNumber={workorder.claimNumber}
                      workorderStatus={{
                        workorderStatusId: workorder.workorderStatusId,
                        name: workorder.workorderStatusName
                      }}
                      program={{
                        id: workorder.programId,
                        name: workorder.programName
                      }}
                      highlightText={debouncedSearchCriteria}
                      preferredContactLabel={t("GlobalSearch.ContactPreference")}
                      onClick={handleWorkorderSelection}
                      updatedAt={workorder.updatedAt}
                      deviceName={workorder.deviceCatalog.name}
                      isRefurbishedDeviceSale={workorder.isRefurbishedDeviceSale || false}
                      clientId={workorder.clientId}
                    />
                  ))}
                  {generateSearchFooter(searchedWorkordersData)}
                </>
              ) : null}
              {searchCategory === chipOptions.claims.searchCategory && searchedArrivalsData?.length ? (
                <>
                  <GreyHorizontalDivider />
                  <QuickSearchResultContainer>
                    <QuickSearchArrivalDetails
                      fullName={searchedArrivalsData[0].customer.fullName}
                      email={searchedArrivalsData[0].customer.primaryEmail}
                      phone={searchedArrivalsData[0].customer.primaryPhone}
                      canCall={false}
                      canEmail={!!searchedArrivalsData[0].customer.canEmail}
                      canSms={false}
                      preferredContactLabel=""
                      arrival={{
                        arrivalId: searchedArrivalsData[0].arrivalId,
                        arrivalStatusId: searchedArrivalsData[0].arrivalStatus.arrivalStatusId,
                        appointment: searchedArrivalsData[0].appointment || "--",
                        isMissed: searchedArrivalsData[0]?.isMissed
                      }}
                      deviceName={searchedArrivalsData[0].customerDevice.deviceName || ""}
                      modelName={searchedArrivalsData[0].customerDevice.modelName || ""}
                      onClick={handleArrivalSelection}
                      storeTimeZone={store.storeTimeZone}
                      clientId={searchedArrivalsData[0].clientId}
                    />
                  </QuickSearchResultContainer>
                </>
              ) : null}
              {partsToDisplay?.length ? (
                <>
                  <GreyHorizontalDivider />
                  <PartSearchContainer>
                    {partsToDisplay.map((parts) => (
                      <QuickSearchPartsDetails
                        key={parts.id}
                        itemName={parts.name}
                        itemNumber={parts.itemNumber}
                        availableCount={parts?.AvailableQuantity}
                        highlightText={debouncedSearchCriteria}
                        onClick={() => handlePartsItemSelection(parts.itemNumber)}
                      />
                    ))}
                  </PartSearchContainer>
                </>
              ) : null}
              {searchedSerialData !== undefined ? (
                <>
                  <GreyHorizontalDivider />
                  <PartSearchContainer>
                    <QuickSearchSerialDetails
                      key={searchedSerialData.$id ?? searchedSerialData.ItemNumber}
                      itemName={searchedSerialData.ProductName}
                      itemNumber={searchedSerialData.ItemNumber}
                      serialNumber={debouncedSearchCriteria}
                      serialStatus={searchedSerialData.SerialStatus}
                      highlightText={debouncedSearchCriteria}
                      setShowModal={openPartDetailModal}
                    />
                  </PartSearchContainer>
                </>
              ) : null}
            </QuickSearchOverlay>
          ) : null}
        </StyledDiv>
      )}
    </>
  );
};

const PartSearchContainer = styled.div`
  display: flex;
  overflow-y: auto;
  flex-direction: column;
`;
const StyledDiv = styled.div<{ shouldScroll: boolean }>`
  display: flex;
  overflow: ${(props) => (props.shouldScroll ? "auto" : "hidden")}
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  justify-content: center;
  align-items: center;
  position: relative;

  .search-field {
    position: relative;
    width: 100%;

    .search-field-loader {
      position: absolute;
      right: 2.5rem;
      top: 50%;
      transform: translateY(-50%);
    }

    .search-text-field {
      width: 100%;
    }
  }
  ${breakpoints.md2} {
    width: 90%;
  }
  ${breakpoints.lg} {
    width: 94%;
  }
  ${breakpoints.xl} {
    width: 89%;
  }
  ${breakpoints.xxl} {
    width: 80%;
  }
`;

const QuickSearchOverlay = styled.div`
  width: 100%;
  padding: 0.5rem 1rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  background: var(--aui-elevated-level-03);
  position: absolute;
  z-index: 1001;
  top: calc(100% + 0.25rem);
  border-radius: 0.5rem;
  box-sizing: border-box;
  box-shadow:
    0px 1px 4px 0px var(--aui-fills-drop-shadow),
    0px 0px 12px 8px var(--aui-fills-drop-shadow);
  max-height: 80vh;
  overflow-y: scroll;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0rem 1rem 0rem;
  gap: 0.5rem;
  color: var(--aui-on-surface);

  .text-looking-for {
    width: 7.5rem;
  }

  ${breakpoints.lg} {
    padding: 0.5rem 0.75rem 1rem 0.75rem;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    align-self: stretch;
  }
`;

const GreyHorizontalDivider = styled.div`
  width: 100%;
  background: var(--aui-outline);
  height: 0.0625rem;
`;

const QuickSearchResultContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledViewResults = styled.div`
  padding: 0 0.75rem;
  height: 3.5rem;
  gap: 0.5rem;
  display: flex;
  align-items: center;
  background-color: var(--aui-surface);
  .view-results-text {
    white-space: pre-wrap;
    display: flex;
    align-items: center;
  }

  &:hover {
    background-color: var(--aui-outline-soft);
    border-radius: 0.5rem;
    cursor: pointer;
  }
`;
